import React, { useEffect, useState } from 'react';
import { Button } from '../components/Button';
import { LeafIcon, AlertTriangleIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, RefreshCcwIcon, BatteryChargingIcon, DumbbellIcon, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ContactSection } from '../components/ContactSection';
import { SuperfoodsFAQSection } from '../components/SuperfoodsFAQSection';
export const SuperfoodsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chlorella' | 'jecmen'>('chlorella');
  const [showBusinessText, setShowBusinessText] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Aktualizovaná funkce pro přesměrování na Green Ways - opraveno pro mobil
  const redirectToGreenWays = () => {
    setIsRedirecting(true);
    // Detekce mobilního zařízení
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    setTimeout(() => {
      if (isMobile) {
        // Pro mobilní zařízení použijeme window.location.href se zvacím kódem
        window.location.href = 'https://www.gw-int.net/kategorie?code=bodyhack';
        // Neodstraňujeme loading screen - zůstane až do přesměrování
      } else {
        // Pro desktop použijeme window.open se zvacím kódem
        window.open('https://www.gw-int.net/kategorie?code=bodyhack', '_blank');
        // Reset redirecting state pouze pro desktop
        setTimeout(() => {
          setIsRedirecting(false);
        }, 500);
      }
    }, 1500);
  };
  // Nová funkce pro scrollování na kontaktní formulář na stejné stránce
  const scrollToContactForm = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <>
      <section className="section section-gradient-green relative py-20 min-h-screen" id="greens" data-id="element-1422">
        {/* Redirecting Overlay for Green Ways */}
        <AnimatePresence data-id="element-1423">
          {isRedirecting && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center" data-id="element-1424">
              <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1
          }} className="text-center" data-id="element-1425">
                <div className="mb-6" data-id="element-1426">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto" data-id="element-1427" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1428">
                  Přesměrovávání na stránky GW
                </h3>
                <p className="text-gray-400" data-id="element-1429">Počkejte chvíli...</p>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        <div className="container mx-auto px-4 section-content" data-id="element-1430">
          {/* Header Section with Animated Background */}
          <div className="relative overflow-hidden mb-10" data-id="element-1431">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1432"></div>
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1433"></div>
            <motion.h2 initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="text-4xl md:text-6xl font-black mb-4 text-center" data-id="element-1434">
              <span className="text-green-500" data-id="element-1435">SUPERPOTRAVINY</span> –{' '}
              <span className="relative" data-id="element-1436">
                <span className="relative z-10" data-id="element-1437">ZELENÉ PALIVO</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-green-500/10 rounded-full -z-0" data-id="element-1438"></span>
              </span>{' '}
              PRO TVÉ{' '}
              <span className="relative" data-id="element-1439">
                <span className="relative z-10" data-id="element-1440">TĚLO</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-green-500/10 rounded-full -z-0" data-id="element-1441"></span>
              </span>{' '}
              I{' '}
              <span className="relative" data-id="element-1442">
                <span className="relative z-10" data-id="element-1443">MYSL</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-green-500/10 rounded-full -z-0" data-id="element-1444"></span>
              </span>
            </motion.h2>
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} className="w-24 h-1 bg-gradient-to-r from-green-500/50 via-green-500 to-green-500/50 mx-auto rounded-full mb-6" data-id="element-1445"></motion.div>
          </div>

          {/* Alert Notice - Styled as a premium notice */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-gray-900/90 to-gray-800/90 p-2.5 md:p-3.5 rounded-lg border-l-2 border-amber-500 shadow-lg" data-id="element-1446">
            <div className="flex items-start gap-2.5" data-id="element-1447">
              <div className="bg-amber-500/20 p-1.5 md:p-2 rounded-full flex-shrink-0" data-id="element-1448">
                <AlertTriangleIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-amber-500" data-id="element-1449" />
              </div>
              <div data-id="element-1450">
                <h4 className="font-bold text-amber-400 mb-0.5 text-xs md:text-sm" data-id="element-1451">
                  UPOZORNĚNÍ! Informace k nákupu superpotravin
                </h4>
                <p className="text-gray-300 text-xs" data-id="element-1452">
                  Pokud klikneš v sekci superpotravin na tlačítko „MÁM ZÁJEM",
                  budeš automaticky přesměrován na oficiální stránky společnosti
                  Green Ways. Důvod je jednoduchý – Green Ways zachovává férový
                  a etický způsob sdílení těchto produktů. Proto jejich prodej
                  neprobíhá přímo na mých stránkách, ale výhradně
                  prostřednictvím oficiálních stránek.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Introduction Section with Enhanced Visual Layout */}
          <div className="max-w-6xl mx-auto mb-16" data-id="element-1453">
            <motion.h3 initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} viewport={{
            once: true
          }} className="text-xl md:text-3xl font-bold mb-6 md:mb-8 text-center" data-id="element-1454">
              <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-green-500/20 rounded-full mr-3 mb-2" data-id="element-1455">
                <LeafIcon className="w-5 h-5 md:w-6 md:h-6 text-green-500" data-id="element-1456" />
              </div>
              <span className="relative" data-id="element-1457">
                <span data-id="element-1458">
                  Změň svoje <span className="text-green-500" data-id="element-1459">zdraví</span> od
                  základů. Objev sílu{' '}
                  <span className="text-green-500" data-id="element-1460">chlorelly</span> a{' '}
                  <span className="text-green-500" data-id="element-1461">ječmene</span>.
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" data-id="element-1462"></span>
              </span>
            </motion.h3>

            <div className="flex flex-col md:flex-row gap-8 items-stretch" data-id="element-1463">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} viewport={{
              once: true
            }} className="w-full md:w-1/2 relative flex items-stretch" data-id="element-1464">
                <div className="rounded-xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(34,197,94,0.5)] relative w-full h-full" data-id="element-1465">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 to-green-500/10 rounded-xl z-10" data-id="element-1466"></div>
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5ZxdroV1pfY1Rq1DzC62ih/mlady-jecmen-green-ways-napoj-15366006533983_1993633d3396bbe13.jpg" alt="Zelený nápoj z mladého ječmene a chlorelly" className="w-full h-full object-cover object-center rounded-xl" style={{
                  minHeight: '500px'
                }} data-id="element-1467" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-xl" data-id="element-1468"></div>
                  <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/60 to-transparent rounded-b-xl" data-id="element-1469"></div>
                </div>
                <div className="absolute -inset-0.5 bg-gradient-to-br from-green-500/30 to-green-900/40 rounded-xl blur-sm -z-10" data-id="element-1470"></div>
              </motion.div>

              <div className="w-full md:w-1/2 flex flex-col" data-id="element-1471">
                <motion.div initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} viewport={{
                once: true
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6 rounded-xl border-l-4 border-green-500 luxury-card mb-4 transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]" data-id="element-1472">
                  <p className="text-sm md:text-lg mb-2.5" data-id="element-1473">
                    <span className="font-semibold text-green-400" data-id="element-1474">
                      Superpotraviny ti nepomůžou jen se svaly.
                    </span>{' '}
                    Mění i <span className="text-green-400" data-id="element-1475">hlavu</span>,{' '}
                    <span className="text-green-400" data-id="element-1476">náladu</span>,{' '}
                    <span className="text-green-400" data-id="element-1477">trávení</span>,{' '}
                    <span className="text-green-400" data-id="element-1478">energii</span> a{' '}
                    <span className="text-green-400" data-id="element-1479">vnitřní klid</span>.
                  </p>
                  <p className="text-sm md:text-lg mb-2.5" data-id="element-1480">
                    Nejsou to žádné trendy doplňky, na které si za měsíc nikdo
                    nevzpomene – zelené potraviny pro mě znamenají{' '}
                    <span className="text-green-400" data-id="element-1481">
                      základní stavební kameny
                    </span>{' '}
                    zdravého těla a silné mysli. A ty samé základy mohou pomoci
                    vybudovat i tobě!
                  </p>
                  <p className="text-sm md:text-lg" data-id="element-1482">
                    <span className="font-semibold text-green-400" data-id="element-1483">
                      Chlorella a ječmen od Green Ways změnily nejen můj život –
                      ale i mému tátovi.
                    </span>{' '}
                    Oba jsme díky nim začali vnímat zásadní rozdíly ve trávení,
                    imunitě i mentální pohodě. A to všechno přirozenou, čistě
                    rostlinnou cestou.
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.5
              }} viewport={{
                once: true
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6 rounded-xl border-l-4 border-green-500 luxury-card mb-4 transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]" data-id="element-1484">
                  <div data-id="element-1485">
                    <p className="text-sm md:text-lg font-medium mb-1.5" data-id="element-1486">
                      Možná si říkáš –{' '}
                      <span className="text-green-400" data-id="element-1487">
                        proč zrovna Green Ways
                      </span>{' '}
                      chlorella a ječmen?
                    </p>
                    <button onClick={() => {
                    const element = document.getElementById('gw-reason-more');
                    if (element) {
                      element.classList.toggle('hidden');
                      element.classList.toggle('block');
                    }
                    const button = document.getElementById('gw-reason-button');
                    if (button) {
                      if (element?.classList.contains('hidden')) {
                        button.textContent = 'Více...';
                      } else {
                        button.textContent = 'Méně';
                      }
                    }
                  }} id="gw-reason-button" className="text-green-500 hover:text-green-400 transition-colors text-xs md:text-sm font-medium flex items-center" data-id="element-1488">
                      Více...
                      <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1489" />
                    </button>
                    <div id="gw-reason-more" className="hidden mt-3 overflow-hidden transition-all duration-300 ease-in-out" data-id="element-1490">
                      <p className="text-sm md:text-lg mb-2.5" data-id="element-1491">
                        Vždyť těch značek je dnes plný internet. Jenže právě v
                        tom je ten rozdíl. Většina produktů se pěstuje někde
                        poblíž, bez ohledu na to, jestli tam vůbec mají ideální
                        podmínky. Green Ways naopak investuje obrovské peníze a
                        úsilí do toho, aby chlorella a ječmen rostly tam, kde
                        mají naprosto přirozené, téměř dokonalé podmínky.
                        Pěstují je na opačných koncích světa – každou
                        superpotravinu zvlášť, přesně tam, kde jí to svědčí
                        nejvíc.
                      </p>
                      <p className="text-sm md:text-lg" data-id="element-1492">
                        Díky tomu si můžeš být jistý, že každý gram, který si
                        dáš do těla, je plný života, živin a účinku – ne jen
                        marketingu. Jsou to živé potraviny, které ti můžou
                        opravdu změnit zdraví.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.7
              }} viewport={{
                once: true
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6 rounded-xl border-l-4 border-green-500 luxury-card mb-4 transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]" data-id="element-1493">
                  <div data-id="element-1494">
                    <p className="text-sm md:text-lg font-medium mb-1.5" data-id="element-1495">
                      A proč zrovna nakupovat{' '}
                      <span className="text-green-400" data-id="element-1496">ode mě</span>?
                    </p>
                    <button onClick={() => {
                    const element = document.getElementById('why-me-more');
                    if (element) {
                      element.classList.toggle('hidden');
                      element.classList.toggle('block');
                    }
                    const button = document.getElementById('why-me-button');
                    if (button) {
                      if (element?.classList.contains('hidden')) {
                        button.textContent = 'Více...';
                      } else {
                        button.textContent = 'Méně';
                      }
                    }
                  }} id="why-me-button" className="text-green-500 hover:text-green-400 transition-colors text-xs md:text-sm font-medium flex items-center" data-id="element-1497">
                      Více...
                      <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1498" />
                    </button>
                    <div id="why-me-more" className="hidden mt-3 overflow-hidden transition-all duration-300 ease-in-out" data-id="element-1499">
                      <p className="text-sm md:text-lg" data-id="element-1500">
                        Protože se vzdělávám přímo od lidí, kteří u Green Ways
                        působí roky a mají za sebou stovky klientů se kterými
                        spolupracovali, jak s užíváním GW, tak s tím, jak
                        podnikat s GW. Také pomáhali uživatelům GW s tím, jak
                        pomoci lidem okolo sebe a zároveň si budovat pasivní
                        příjem (není to povinné podnikat, člověk může pouze
                        užívat). Učím se od těch nejlepších, abych byl schopný
                        předat dál, informace, které mužou člověku ne jen pomoct
                        zdravotně ale i finančně. Nechci ti něco jen prodat a
                        zmizet. Když mi napíšeš, poradím ti s dávkováním a
                        pomůžu s výběrem.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.8
              }} viewport={{
                once: true
              }} className="relative mt-auto" data-id="element-1501">
                  <Button variant="primary" size="lg" onClick={() => {
                  const element = document.getElementById('greens-products');
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }
                }} className="luxury-button w-full text-sm md:text-base" data-id="element-1502">
                    <span className="relative z-10" data-id="element-1503">CHCI TO VYZKOUŠET</span>
                  </Button>
                  <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-1504"></div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Bull Section with Enhanced Visual Elements */}
          <div className="flex flex-col md:flex-row gap-12 mb-28" data-id="element-1505">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-1506">
              <h3 className="text-xl md:text-3xl font-extrabold mb-6 md:mb-8 flex items-center" data-id="element-1507">
                Viděl jsi někdy býka jíst{' '}
                <span className="text-green-500 ml-1 relative" data-id="element-1508">
                  maso
                  <span className="absolute bottom-1 left-0 w-full h-1 bg-green-500/30 rounded-full" data-id="element-1509"></span>
                </span>
                ?
              </h3>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-7 rounded-xl border-l-4 border-green-500 luxury-card mb-6 shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]" data-id="element-1510">
                <p className="text-sm md:text-lg mb-4" data-id="element-1511">
                  Zamyslel ses někdy, proč vlastně jíme maso kvůli bílkovinám –
                  když{' '}
                  <span className="text-green-400" data-id="element-1512">
                    zvířata, která mají obrovskou sílu, svaly a energii
                  </span>
                  , se živí jen rostlinami?
                </p>
                <p className="text-sm md:text-lg mb-4" data-id="element-1513">
                  Příkladem je býk – jedno z{' '}
                  <span className="text-green-400" data-id="element-1514">
                    nejsilnějších zvířat na planetě
                  </span>
                  . Nemusí sníst ani gram masa, a přesto je masivní, svalnatý a
                  plný energie. Podobně gorily, koně nebo sloni – všechna tato
                  silná zvířata staví svou sílu na rostlinné stravě.
                </p>
                <p className="text-sm md:text-lg mb-4" data-id="element-1515">
                  Masožravci mají krátké trávicí ústrojí a ostré čelisti
                  uzpůsobené ke kousání masa. My lidé máme zuby, délku střev i
                  trávení mnohem podobnější býložravcům.
                </p>
                <p className="text-sm md:text-lg mb-4" data-id="element-1516">
                  To neznamená, že musíš přestat jíst maso.
                </p>
                <p className="text-sm md:text-lg" data-id="element-1517">
                  Ale pokud jíš maso,{' '}
                  <span className="text-green-400" data-id="element-1518">
                    zelené superpotraviny ti pomůžou lépe vstřebat živiny z masa
                    i dalších jídel
                  </span>{' '}
                  – což vede k:
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8" data-id="element-1519">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.1
              }} viewport={{
                once: true
              }} whileHover={{
                y: -5,
                boxShadow: '0 15px 30px -5px rgba(34, 197, 94, 0.3)'
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-3 md:p-5 rounded-lg text-center border-t-2 border-green-500 luxury-card transition-all duration-300" data-id="element-1520">
                  <div className="bg-green-500/20 p-2 md:p-4 rounded-full inline-flex mb-2 md:mb-4 relative" data-id="element-1521">
                    <RefreshCcwIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-1522" />
                    <div className="absolute inset-0 bg-green-500/10 rounded-full animate-pulse" data-id="element-1523"></div>
                  </div>
                  <p className="text-xs md:text-base font-medium text-green-400" data-id="element-1524">
                    Rychlejší regenerace
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.2
              }} viewport={{
                once: true
              }} whileHover={{
                y: -5,
                boxShadow: '0 15px 30px -5px rgba(34, 197, 94, 0.3)'
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-3 md:p-5 rounded-lg text-center border-t-2 border-green-500 luxury-card transition-all duration-300" data-id="element-1525">
                  <div className="bg-green-500/20 p-2 md:p-4 rounded-full inline-flex mb-2 md:mb-4 relative" data-id="element-1526">
                    <BatteryChargingIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-1527" />
                  </div>
                  <p className="text-xs md:text-base font-medium text-green-400" data-id="element-1528">
                    Zdroj důležitých látek
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.3
              }} viewport={{
                once: true
              }} whileHover={{
                y: -5,
                boxShadow: '0 15px 30px -5px rgba(34, 197, 94, 0.3)'
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-3 md:p-5 rounded-lg text-center border-t-2 border-green-500 luxury-card transition-all duration-300" data-id="element-1529">
                  <div className="bg-green-500/20 p-2 md:p-4 rounded-full inline-flex mb-2 md:mb-4 relative" data-id="element-1530">
                    <DumbbellIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-1531" />
                    <div className="absolute inset-0 bg-green-500/10 rounded-full animate-pulse" data-id="element-1532"></div>
                  </div>
                  <p className="text-xs md:text-base font-medium text-green-400" data-id="element-1533">
                    Rychlejší výsledky
                  </p>
                </motion.div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-7 rounded-xl border-l-4 border-green-500 luxury-card mb-8 shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]" data-id="element-1534">
                <p className="text-sm md:text-lg" data-id="element-1535">
                  A díky tomu, že obsahují až{' '}
                  <span className="text-green-400 font-bold" data-id="element-1536">
                    61 % bílkovin
                  </span>
                  , přirozeně přispívají i k růstu svalové hmoty a celkové
                  regeneraci – a to čistě přírodní cestou.
                </p>
              </div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} viewport={{
              once: true
            }} className="relative" data-id="element-1537">
                <div className="flex justify-center" data-id="element-1538">
                  <Button variant="primary" size="lg" onClick={() => {
                  const element = document.getElementById('greens-products');
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }
                }} className="luxury-button px-8 md:px-10 text-sm md:text-base" data-id="element-1539">
                    CHCI TO VYZKOUŠET
                  </Button>
                </div>
                <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse pointer-events-none" data-id="element-1540"></div>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-1541">
              <div className="h-full flex items-center justify-center" data-id="element-1542">
                <div className="relative w-full h-full" data-id="element-1543">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kcKykjgtVaWmYheJCxoG4L/ChatGPT_Image_Aug_27%2C_2025%2C_07_44_58_PM.png" alt="Býk jíst trávu" className="rounded-xl shadow-2xl w-full h-full object-cover object-[60%_center] md:object-[65%_center]" data-id="element-1544" />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-transparent rounded-xl" data-id="element-1545"></div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Combination Section with Enhanced Design */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto mb-28 relative" data-id="element-1546">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1547"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1548"></div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_40px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-1549">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1550"></div>

              <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center relative" data-id="element-1551">
                <span className="relative inline-block" data-id="element-1552">
                  <span className="relative z-10" data-id="element-1553">
                    KOMBINACE, KTERÁ FUNGUJE
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1554"></span>
                </span>
              </h3>

              <div className="flex items-center justify-center mb-6 md:mb-8" data-id="element-1555">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-green-500/20 rounded-full flex items-center justify-center mr-5" data-id="element-1556">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4QcMtVwUjEurrV4jP7Dst9/Screenshot_2025-07-22_at_18.05.35.png" alt="Chlorella" className="w-10 h-10 md:w-12 md:h-12 object-contain" data-id="element-1557" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-green-500" data-id="element-1558">
                  +
                </div>
                <div className="w-16 h-16 md:w-20 md:h-20 bg-green-500/20 rounded-full flex items-center justify-center ml-5" data-id="element-1559">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/t3D6MtSnuk72yXvHJPn46o/Screenshot_2025-07-22_at_18.05.55.png" alt="Ječmen" className="w-10 h-10 md:w-12 md:h-12 object-contain" data-id="element-1560" />
                </div>
              </div>

              <p className="text-sm md:text-lg mb-4 md:mb-6 text-center" data-id="element-1561">
                Když se <span className="text-green-400" data-id="element-1562">chlorella</span> a{' '}
                <span className="text-green-400" data-id="element-1563">ječmen</span> užívají společně,
                jejich účinky se navzájem{' '}
                <span className="text-green-400" data-id="element-1564">posilují</span>.
              </p>

              <div className="bg-black/30 p-4 md:p-6 rounded-lg mb-6 md:mb-8 border border-green-500/20" data-id="element-1565">
                <p className="text-sm md:text-lg mb-4 md:mb-6 text-center" data-id="element-1566">
                  Výsledek?{' '}
                  <span className="text-green-400" data-id="element-1567">
                    Rychlejší detoxikace těla
                  </span>
                  , <span className="text-green-400" data-id="element-1568">víc energie</span>,{' '}
                  <span className="text-green-400" data-id="element-1569">lepší regenerace</span>,{' '}
                  <span className="text-green-400" data-id="element-1570">silnější imunita</span>,{' '}
                  <span className="text-green-400" data-id="element-1571">zdravější trávení</span> a{' '}
                  <span className="text-green-400" data-id="element-1572">vyrovnaná mysl</span>.
                </p>
                <p className="text-sm md:text-lg mb-0 text-center" data-id="element-1573">
                  Přesně to, co potřebuješ, když chceš být{' '}
                  <span className="text-green-400" data-id="element-1574">silný</span>,{' '}
                  <span className="text-green-400" data-id="element-1575">zdravý</span> a{' '}
                  <span className="text-green-400" data-id="element-1576">v klidu</span>. Když to navíc
                  spojíš s mým tréninkovým plánem, správným užíváním doplňků a
                  disciplínou – transformace těla je nevyhnutelná!
                </p>
              </div>

              <div className="text-center relative" data-id="element-1577">
                <div className="flex justify-center" data-id="element-1578">
                  <Button variant="primary" size="lg" onClick={redirectToGreenWays} className="luxury-button px-8 md:px-10 text-sm md:text-base" data-id="element-1579">
                    OBJEDNAT TEĎ
                  </Button>
                </div>
                <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse pointer-events-none" data-id="element-1580"></div>
              </div>
            </div>
          </motion.div>

          {/* Products Section with Enhanced Cards */}
          <div id="greens-products" className="max-w-5xl mx-auto mb-20" data-id="element-1581">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-1582">
              <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1583"></div>

              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-center" data-id="element-1584">
                <span className="relative inline-block" data-id="element-1585">
                  <span className="relative z-10" data-id="element-1586">
                    MOŽNOSTI NÁKUPU SUPERPOTRAVIN
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1587"></span>
                </span>
              </h3>

              <div className="w-24 h-1 bg-gradient-to-r from-green-500/50 via-green-500 to-green-500/50 mx-auto rounded-full mb-8" data-id="element-1588"></div>
            </motion.div>

            {/* One-time Purchase Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-1589">
              <div className="absolute -right-10 top-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1590"></div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden luxury-card relative border border-green-500/30" data-id="element-1591">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1592"></div>

                <div className="p-1 bg-green-500 text-white font-bold text-center text-xs" data-id="element-1593">
                  MOŽNOST 1
                </div>

                <div className="p-4 md:p-6" data-id="element-1594">
                  <h4 className="text-lg md:text-xl font-bold mb-3 text-center flex items-center justify-center" data-id="element-1595">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mr-2" data-id="element-1596">
                      <span className="text-green-500 font-bold text-xs" data-id="element-1597">
                        1
                      </span>
                    </div>
                    JEDNORÁZOVÝ NÁKUP
                  </h4>

                  <p className="text-xs md:text-sm mb-3 text-center" data-id="element-1598">
                    Chceš si to nejdřív jen{' '}
                    <span className="text-green-400" data-id="element-1599">zkusit</span>? Můžeš.
                    Objednej si 1 balení chlorelly a/nebo ječmene a sleduj, jak
                    se budeš cítit.
                  </p>

                  <div className="bg-black/30 p-3 md:p-4 rounded-lg mb-4 border border-green-500/20" data-id="element-1600">
                    <p className="text-xs mb-2" data-id="element-1601">
                      Ale buď <span className="text-green-400" data-id="element-1602">trpělivý</span> –
                      reálné výsledky nepřijdou za pár dní, tělo potřebuje čas.
                      Tohle není zázrak. Je to{' '}
                      <span className="text-green-400" data-id="element-1603">přírodní síla</span>,
                      která pracuje každý den – a čím déle ji užíváš, tím víc tě
                      mění. Proto doporučuji hodnotit účinky superpotravin až po
                      delší době pravidelného užívání, kdy se mohou projevit
                      skutečné výsledky.
                    </p>
                    <p className="text-xs mb-0" data-id="element-1604">
                      Jedna chlorela a jeden ječmen, vydrží při pravidelném
                      užívání cca{' '}
                      <span className="text-green-400" data-id="element-1605">
                        měsíc na jednu osobu
                      </span>
                      . Samozřejmě dávky se liší podle váhy člověka a aktuálního
                      potřeby a cíle.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:flex md:flex-row gap-3 justify-center" data-id="element-1606">
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-1607">
                      <div className="flex justify-center mb-2 relative" data-id="element-1608">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-1609"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-1610">
                          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4QcMtVwUjEurrV4jP7Dst9/Screenshot_2025-07-22_at_18.05.35.png" alt="Chlorella" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-1611" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-1612">
                        Chlorella
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-1613">
                        Detoxikace a imunita
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-1614">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-1615">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-1616">
                      <div className="flex justify-center mb-2 relative" data-id="element-1617">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-1618"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-1619">
                          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/t3D6MtSnuk72yXvHJPn46o/Screenshot_2025-07-22_at_18.05.55.png" alt="Ječmen" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-1620" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-1621">
                        Ječmen
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-1622">
                        Energie a regenerace
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-1623">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-1624">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Long-term Purchase Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-1625">
              <div className="absolute -left-10 top-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1626"></div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden luxury-card relative border border-green-500/30" data-id="element-1627">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1628"></div>

                <div className="p-1 bg-green-500 text-white font-bold text-center text-xs" data-id="element-1629">
                  MOŽNOST 2
                </div>

                <div className="p-4 md:p-6" data-id="element-1630">
                  <h4 className="text-base md:text-lg font-bold mb-3 text-center flex items-center justify-center" data-id="element-1631">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mr-2" data-id="element-1632">
                      <span className="text-green-500 font-bold text-xs" data-id="element-1633">
                        2
                      </span>
                    </div>
                    DLOUHODOBÉ UŽÍVÁNÍ : KUP SI VÍCE BALÍČKŮ NARÁZ, BEZ ZÁVAZKU
                  </h4>

                  <p className="text-xs md:text-sm mb-3 text-center" data-id="element-1634">
                    Chceš jít do toho{' '}
                    <span className="text-green-400" data-id="element-1635">naplno</span> a cítit{' '}
                    <span className="text-green-400" data-id="element-1636">skutečné změny</span>? Pak
                    je klíčové užívat chlorellu a ječmen dlouhodobě a
                    pravidelně. Při pravidelném a dlouhodobém užívání se
                    postupně projeví účinky, jako například:
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4" data-id="element-1637">
                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.1
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-1638">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-1639" />
                      <span className="text-green-400 text-[10px]" data-id="element-1640">
                        Více energie během dne
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-1641">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-1642" />
                      <span className="text-green-400 text-[10px]" data-id="element-1643">
                        Rychlejší regenerace po tréninku
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.3
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-1644">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-1645" />
                      <span className="text-green-400 text-[10px]" data-id="element-1646">
                        Silnější imunita a zdravé trávení
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.4
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-1647">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-1648" />
                      <span className="text-green-400 text-[10px]" data-id="element-1649">
                        Lepší spánek a psychická pohoda
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.5
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-1650">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-1651" />
                      <span className="text-green-400 text-[10px]" data-id="element-1652">
                        Podpora růstu svalů
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.6
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-1653">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-1654" />
                      <span className="text-green-400 text-[10px]" data-id="element-1655">
                        Mentální výkon a čistá hlava
                      </span>
                    </motion.div>
                  </div>

                  <div className="bg-black/30 p-3 rounded-lg mb-4 border border-green-500/20" data-id="element-1656">
                    <p className="text-xs mb-2 text-center" data-id="element-1657">
                      Tahle možnost je pro lidi, kteří si chcí koupit GW{' '}
                      <span className="text-green-400" data-id="element-1658">
                        jednorázově větší množství
                      </span>{' '}
                      na delší dobu{' '}
                      <span className="text-green-400" data-id="element-1659">
                        bez žádného závazku
                      </span>
                      .
                    </p>
                    <p className="text-xs mb-0 text-center font-semibold" data-id="element-1660">
                      <span className="text-green-400" data-id="element-1661">
                        Pokud nakoupíte 3 produkty Superpotravin, získáte 1
                        zdarma!
                      </span>{' '}
                      Stačí se zaregistrovat při dokončení objednávky!
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:flex md:flex-row gap-3 justify-center" data-id="element-1662">
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-1663">
                      <div className="flex justify-center mb-2 relative" data-id="element-1664">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-1665"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-1666">
                          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4QcMtVwUjEurrV4jP7Dst9/Screenshot_2025-07-22_at_18.05.35.png" alt="Chlorella" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-1667" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-1668">
                        Chlorella
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-1669">
                        Detoxikace a imunita
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-1670">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-1671">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-1672">
                      <div className="flex justify-center mb-2 relative" data-id="element-1673">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-1674"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-1675">
                          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/t3D6MtSnuk72yXvHJPn46o/Screenshot_2025-07-22_at_18.05.55.png" alt="Ječmen" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-1676" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-1677">
                        Ječmen
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-1678">
                        Energie a regenerace
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-1679">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-1680">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Autoship Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-1681">
              <div className="absolute -right-10 bottom-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1682"></div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden luxury-card relative border border-green-500/30" data-id="element-1683">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1684"></div>

                <div className="p-1 bg-green-500 text-white font-bold text-center text-xs" data-id="element-1685">
                  MOŽNOST 3
                </div>

                <div className="absolute top-0 right-0 mt-[2px] mr-[-5px] bg-gradient-to-r from-green-600 to-green-500 text-white text-[10px] md:text-xs font-bold px-3 py-0.5 rounded-full shadow-lg border-2 border-white z-10 transform rotate-2 animate-pulse" data-id="element-1686">
                  NEJPRODÁVANĚJŠÍ
                </div>

                <div className="p-4 md:p-6" data-id="element-1687">
                  {/* Nadpis a základní popis */}
                  <h4 className="text-lg md:text-xl font-bold mb-3 text-center" data-id="element-1688">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-1689">
                      <span className="text-green-500 font-bold text-xs" data-id="element-1690">
                        3
                      </span>
                    </div>
                    CHCI, ABY MI SUPERPOTRAVINY CHODILY KAŽDÝ MĚSÍC (AUTOSHIP)
                  </h4>

                  <p className="text-xs md:text-sm mb-4 text-center" data-id="element-1691">
                    Tahle možnost je pro tebe, pokud chceš mít svůj{' '}
                    <span className="text-green-400" data-id="element-1692">
                      pravidelný přísun zdraví
                    </span>{' '}
                    a zároveň{' '}
                    <span className="text-green-400" data-id="element-1693">pomáhat svému okolí</span>.
                  </p>

                  {/* Co dostaneš */}
                  <div className="bg-black/30 p-3 rounded-lg mb-4 border border-green-500/20" data-id="element-1694">
                    <p className="text-center mb-1 text-sm font-medium text-white" data-id="element-1695">
                      Každý měsíc ti dorazí:
                    </p>
                    <p className="text-center text-xl font-bold text-green-400" data-id="element-1696">
                      2× Chlorella + 2× Ječmen
                    </p>
                  </div>

                  {/* Proč tolik */}
                  <div className="mb-4" data-id="element-1697">
                    <div className="flex items-center mb-2" data-id="element-1698">
                      <div className="w-1 h-8 bg-green-500 rounded-full mr-3" data-id="element-1699"></div>
                      <h5 className="text-sm font-medium text-green-400" data-id="element-1700">
                        Proč tolik?
                      </h5>
                    </div>

                    <div className="flex flex-col md:flex-row gap-3 mb-3" data-id="element-1701">
                      <div className="flex-1 bg-black/20 p-3 rounded-lg border border-green-500/10" data-id="element-1702">
                        <p className="text-xs text-center" data-id="element-1703">
                          <span className="text-green-400 font-bold" data-id="element-1704">
                            1× Chlorella
                          </span>{' '}
                          +{' '}
                          <span className="text-green-400 font-bold" data-id="element-1705">
                            1× Ječmen
                          </span>
                          <br data-id="element-1706" />
                          pro tebe
                        </p>
                      </div>

                      <div className="flex items-center justify-center" data-id="element-1707">
                        <span className="text-green-500" data-id="element-1708">+</span>
                      </div>

                      <div className="flex-1 bg-black/20 p-3 rounded-lg border border-green-500/10" data-id="element-1709">
                        <p className="text-xs text-center" data-id="element-1710">
                          <span className="text-green-400 font-bold" data-id="element-1711">
                            1× Chlorella
                          </span>{' '}
                          +{' '}
                          <span className="text-green-400 font-bold" data-id="element-1712">
                            1× Ječmen
                          </span>
                          <br data-id="element-1713" />
                          pro rodinu/kamarády
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Co je nejlepší */}
                  <div className="mb-4" data-id="element-1714">
                    <div className="flex items-center mb-2" data-id="element-1715">
                      <div className="w-1 h-8 bg-green-500 rounded-full mr-3" data-id="element-1716"></div>
                      <h5 className="text-sm font-medium text-green-400" data-id="element-1717">
                        A co je na tom nejlepší?
                      </h5>
                    </div>

                    <div className="bg-black/20 p-3 rounded-lg border border-green-500/10" data-id="element-1718">
                      <p className="text-xs mb-0" data-id="element-1719">
                        Díky autoshipu dostáváš{' '}
                        <span className="text-green-400 font-bold" data-id="element-1720">
                          1 chlorellu nebo 1 ječmen ZDARMA každý měsíc
                        </span>
                        . To znamená:{' '}
                        <span className="text-green-400" data-id="element-1721">pomáháš sobě</span> i{' '}
                        <span className="text-green-400" data-id="element-1722">druhým</span>, ale
                        zároveň máš{' '}
                        <span className="text-green-400" data-id="element-1723">
                          jeden balíček měsíčně zdarma
                        </span>
                        .
                      </p>
                    </div>
                  </div>

                  {/* Cena */}
                  <div className="bg-gradient-to-br from-green-900/30 to-gray-900 p-4 rounded-xl mb-5 border border-green-500/30 shadow-[0_10px_25px_-5px_rgba(34,197,94,0.2)]" data-id="element-1724">
                    <h5 className="text-base font-bold mb-2 text-center" data-id="element-1725">
                      CENA AUTOSHIPU NA JEDEN MĚSÍC
                    </h5>

                    <p className="text-2xl font-bold text-center text-green-400 mb-3" data-id="element-1726">
                      3600 Kč
                    </p>

                    <div className="mb-2" data-id="element-1727">
                      <p className="text-xs text-center font-bold text-white" data-id="element-1728">
                        Proč je to{' '}
                        <span className="text-green-400" data-id="element-1729">výhodné</span>?
                      </p>
                    </div>

                    <div className="bg-black/30 p-3 rounded-lg" data-id="element-1730">
                      <p className="text-xs mb-3 text-center" data-id="element-1731">
                        Tohle je cena, kterou by si normálně{' '}
                        <span className="text-green-400" data-id="element-1732">
                          zaplatil za 3 balíčky
                        </span>{' '}
                        (například 2× chlorella, 1× ječmen). Jediný rozdíl v
                        autoshipu od normálního nákupu je, že ti objednávka bude
                        chodit,{' '}
                        <span className="text-green-400" data-id="element-1733">
                          aniž by si ji musel zadávat, včetně slevy každý měsíc!
                        </span>
                      </p>

                      <div className="flex justify-center" data-id="element-1734">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-3 rounded-lg border border-green-500/30 shadow-[0_5px_15px_-3px_rgba(34,197,94,0.3)] inline-block mx-auto" data-id="element-1735">
                          <p className="text-center block font-bold text-sm md:text-base" data-id="element-1736">
                            <span className="text-green-400" data-id="element-1737">
                              HODNOTA ZDARMA : 1200 Kč
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tlačítka */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1738">
                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4
                  }} viewport={{
                    once: true
                  }} className="relative" data-id="element-1739">
                      <Button variant="primary" size="md" onClick={redirectToGreenWays} className="luxury-button px-6 py-3 text-sm w-full sm:w-auto" data-id="element-1740">
                        MÁM ZÁJEM!
                      </Button>
                      <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-1741"></div>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} className="relative" data-id="element-1742">
                      <Button variant="outline" size="md" onClick={scrollToContactForm} className="shine py-3 px-6 text-sm w-full sm:w-auto" data-id="element-1743">
                        CHCI PODNIKAT S GW
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Business Section */}
            <div id="gw-business" className="max-w-4xl mx-auto" data-id="element-1744">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} viewport={{
              once: true
            }} className="relative" data-id="element-1745">
                <div className="absolute -left-10 top-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1746"></div>
                <div className="absolute -right-10 bottom-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1747"></div>

                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_25px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-1748">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1749"></div>
                  <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1750"></div>
                  <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1751"></div>

                  <h4 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center relative" data-id="element-1752">
                    <span className="relative inline-block" data-id="element-1753">
                      <span className="relative z-10" data-id="element-1754">
                        CHCEŠ PODNIKAT S{' '}
                        <span className="text-green-500" data-id="element-1755">GREEN WAYS</span>?
                      </span>
                      <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1756"></span>
                    </span>
                  </h4>

                  <div className="text-center mb-4 md:mb-6" data-id="element-1757">
                    <button onClick={() => setShowBusinessText(!showBusinessText)} className="text-green-500 hover:text-green-400 transition-colors text-sm md:text-lg font-medium flex items-center mx-auto bg-black/30 px-4 md:px-5 py-3 md:py-2 rounded-full border border-green-500/30 hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all duration-300 relative z-20 touch-manipulation" style={{
                    WebkitTapHighlightColor: 'transparent'
                  }} data-id="element-1758">
                      {showBusinessText ? <>
                          zobrazit méně
                          <ChevronUpIcon className="w-4 h-4 md:w-5 md:h-5 ml-1" data-id="element-1759" />
                        </> : <>
                          chci vědět více...
                          <ChevronDownIcon className="w-4 h-4 md:w-5 md:h-5 ml-1" data-id="element-1760" />
                        </>}
                    </button>
                  </div>

                  <AnimatePresence data-id="element-1761">
                    {showBusinessText && <motion.div initial={{
                    opacity: 0,
                    maxHeight: 0
                  }} animate={{
                    opacity: 1,
                    maxHeight: 2000
                  }} exit={{
                    opacity: 0,
                    maxHeight: 0
                  }} transition={{
                    duration: 0.6,
                    ease: [0.4, 0, 0.2, 1]
                  }} style={{
                    willChange: 'max-height, opacity',
                    transform: 'translateZ(0)'
                  }} className="overflow-hidden" data-id="element-1762">
                        <div className="mt-4 mb-6 md:mb-8" data-id="element-1763">
                          <div className="bg-black/20 p-5 rounded-xl border border-green-500/20 mb-4" data-id="element-1764">
                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-1765">
                              Možná už teď používáš{' '}
                              <span className="text-green-400" data-id="element-1766">chlorellu</span>{' '}
                              nebo{' '}
                              <span className="text-green-400" data-id="element-1767">ječmen</span> od{' '}
                              <span className="text-green-400" data-id="element-1768">Green Ways</span>
                              . Nebo o nich alespoň{' '}
                              <span className="text-green-400" data-id="element-1769">přemýšlíš</span>.
                              Možná už cítíš, že ti to pomáhá – víc{' '}
                              <span className="text-green-400" data-id="element-1770">energie</span>,
                              lepší{' '}
                              <span className="text-green-400" data-id="element-1771">trávení</span>,
                              silnější{' '}
                              <span className="text-green-400" data-id="element-1772">imunita</span>. A
                              pokud ne ty, tak třeba někdo ve tvé rodině. A
                              právě tady to začíná mít smysl.
                            </p>

                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-1773">
                              Protože tohle není jen o produktech. Je to o{' '}
                              <span className="text-green-400" data-id="element-1774">
                                skutečné zkušenosti, která má sílu změnit život
                              </span>
                              . A jakmile jednou poznáš, co s tebou tahla změna
                              udělá, začneš přemýšlet nad tím, jak to dostat
                              mezi ostatní lidi, kterým by to mohlo pomoct –
                              tvým kamarádům, rodině, kolegům. A v tu chvíli
                              pochopíš, že přesně o tom je{' '}
                              <span className="text-green-400" data-id="element-1775">
                                podnikání s Green Ways
                              </span>
                              .
                            </p>

                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-1776">
                              <span className="text-green-400" data-id="element-1777">Green Ways</span>{' '}
                              není žádné obyčejné "sdílej a vyděláš" MLM. Tohle
                              funguje jen tehdy, když{' '}
                              <span className="text-green-400" data-id="element-1778">
                                sám věříš tomu, co děláš
                              </span>{' '}
                              – protože máš vlastní příběh. Víš, co ti{' '}
                              <span className="text-green-400" data-id="element-1779">
                                zelené potraviny
                              </span>{' '}
                              daly. Máš výsledky. A když se bavíš s někým
                              dalším, mluvíš ze srdce, ne z letáku. Nemusíš si
                              nic vymýšlet. Jen sdílíš vlastní zkušenost – a
                              lidi to poznají. Tohle podnikání nestojí na
                              prodeji za každou cenu. Stojí na důvěře, skutečné
                              změně a upřímnosti. A to je důvod, proč funguje.
                            </p>

                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-1780">
                              A pokud teď přemýšlíš jen nad tím, že jsi našel
                              produkt, na kterém bys mohl vydělat – ale sám jsi
                              ho ještě nevyzkoušel a nemáš s ním žádnou
                              zkušenost, tak tahla část není určená pro tebe.
                              Nejprve si dej šanci zažít, co tyhle potraviny
                              dokážou s tvým tělem i psychikou, a pak uvidíš
                              sám, že tě to přirozeně povede k tomu, pomáhat
                              dalším lidem. Dřív ne.
                            </p>

                            <p className="text-xs md:text-lg mb-0" data-id="element-1781">
                              Je to cesta, jak si{' '}
                              <span className="text-green-400" data-id="element-1782">
                                vytvořit pasivní příjem
                              </span>
                              , ale zároveň{' '}
                              <span className="text-green-400" data-id="element-1783">
                                pomáhat druhým lidem
                              </span>
                              . Nemusíš být nutriční specialista ani fitness
                              trenér. Stačí, že si tím projdeš sám. Že víš, jak
                              se cítíš s a bez zelených potravin. A že máš chuť
                              to poslat dál.
                            </p>

                            <p className="text-xs md:text-lg mb-0" data-id="element-1784">
                              Zároveň se tím otevřou nové možnosti – můžeš se
                              stát součástí
                              <span className="text-green-400" data-id="element-1785">
                                {' '}
                                komunity lidí, kteří žijí zdravě
                              </span>
                              , mají pozitivní přístup k životu a chtějí se
                              posouvat dál. Lidi, co se navzájem podporují,
                              inspirují a sdílí know-how, aby každý mohl růst –
                              zdravotně, osobně i finančně. Tohle prostředí tě
                              může opravdu změnit. Nejen tvé tělo, ale i způsob,
                              jak přemýšlíte. A nejlepší na tom je, že v tom
                              nejsi sám. Budou tady pro tebe lidi, kteří mají
                              roky praxí za sebou a kteří ti jsou schopni předat
                              plán přesně krok po kroku, od úplného začátku s{' '}
                              <span className="text-green-400" data-id="element-1786">
                                podnikáním
                              </span>{' '}
                              až k tomu, že budeš mít pod sebou systém kterému
                              nejen zdravotně pomáháš, ale také na kterých si
                              můžeš tvořit{' '}
                              <span className="text-green-400" data-id="element-1787">
                                pasivní příjem
                              </span>
                              . Pokud tě to zaujme, rád ti ukážu, jak s{' '}
                              <span className="text-green-400" data-id="element-1788">Green Ways</span>{' '}
                              začít podnikat. Probereme spolu možnosti
                              spolupráce, ukážu ti, jak{' '}
                              <span className="text-green-400" data-id="element-1789">
                                zelené potraviny
                              </span>{' '}
                              přirozeně zapojit do každodenního života, jak o
                              nich mluvit, i jak si tím můžeš vybudovat vlastní
                              cestu ke zdravému já, ale i ke svobodě.
                            </p>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="text-center relative" data-id="element-1790">
                    <div className="absolute inset-0 bg-green-500/10 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-1791"></div>
                    <motion.div whileHover={{
                    scale: 1.05
                  }} transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 10
                  }} className="relative z-10" data-id="element-1792">
                      <Button variant="primary" size="lg" onClick={scrollToContactForm} className="luxury-button px-8 md:px-10 text-sm md:text-base mx-auto" data-id="element-1793">
                        MÁM ZÁJEM O PODNIKÁNÍ S GW
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Vše, co potřebujete vědět o superpotravinách */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="mt-16 max-w-4xl mx-auto" data-id="element-1794">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_40px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-1795">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1796"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1797"></div>
                <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1798"></div>

                <h4 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center relative" data-id="element-1799">
                  <span className="relative inline-block" data-id="element-1800">
                    <span className="relative z-10" data-id="element-1801">
                      VŠE, CO POTŘEBUJETE VĚDĚT O{' '}
                      <span className="text-green-500" data-id="element-1802">SUPERPOTRAVINÁCH</span>
                    </span>
                    <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1803"></span>
                  </span>
                </h4>

                <p className="text-center text-gray-300 max-w-2xl mx-auto mb-8" data-id="element-1804">
                  Kompletní průvodce zelenými superpotravinami - vše od účinků a
                  dávkování po detoxikaci, nemoci a kvalitu produktů. Doporučuji
                  si pročíst tuhle sekci před užíváním superpotravin.
                </p>

                <div className="text-center relative" data-id="element-1805">
                  <motion.div whileHover={{
                  scale: 1.05
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 10
                }} className="relative z-10" data-id="element-1806">
                    <Button variant="primary" size="lg" onClick={() => navigate('/superpotraviny-info')} className="luxury-button px-8 py-4 md:px-10 text-base md:text-lg mx-auto" data-id="element-1807">
                      OTEVŘÍT PRŮVODCE SUPERPOTRAVINAMI
                    </Button>
                  </motion.div>
                  <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-1808"></div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* FAQ Section */}
          <SuperfoodsFAQSection data-id="element-1809" />

          {/* Back to Home Button - Elegant & Luxurious */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="py-16 md:py-24 flex justify-center items-center" data-id="element-1810">
            <button onClick={() => {
            navigate('/');
            // Scroll to top immediately after navigation
            setTimeout(() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }, 100);
          }} className="group relative px-8 py-4 bg-gray-900/50 hover:bg-gray-900/80 backdrop-blur-sm border border-white/10 hover:border-green-500/50 rounded-full transition-all duration-500 ease-out overflow-hidden shadow-lg hover:shadow-[0_0_25px_rgba(34,197,94,0.15)]" data-id="element-1811">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" data-id="element-1812" />
              <span className="relative flex items-center gap-3 text-gray-400 group-hover:text-white transition-colors duration-300 font-medium tracking-widest text-sm uppercase" data-id="element-1813">
                <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1 text-green-500/70 group-hover:text-green-400" data-id="element-1814" />
                Zpět na hlavní stránku
              </span>
            </button>
          </motion.div>
        </div>

        <div className="section-divider divider-tilt" data-id="element-1815"></div>
      </section>
      <ContactSection data-id="element-1816" />
    </>;
};